import React, { useState, useMemo } from 'react'
import './DataVisualization.css'

interface EmotionData {
  emotion: string
  count: number
  percentage: number
  color: string
}

interface ActivityData {
  activity: string
  count: number
  hours: number
}

interface MonthlyData {
  month: string
  memories: number
  goals: number
  emotions: number
}

interface RelationshipStats {
  totalDays: number
  totalMemories: number
  totalGoals: number
  completedGoals: number
  totalEmotions: number
  averageHappiness: number
}

const DataVisualization: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'emotions' | 'activities' | 'trends'>('overview')

  // 模拟数据
  const relationshipStats: RelationshipStats = {
    totalDays: 365,
    totalMemories: 48,
    totalGoals: 12,
    completedGoals: 8,
    totalEmotions: 156,
    averageHappiness: 8.5
  }

  const emotionData: EmotionData[] = [
    { emotion: '开心', count: 45, percentage: 35, color: '#FFD93D' },
    { emotion: '甜蜜', count: 38, percentage: 30, color: '#FF6B9D' },
    { emotion: '兴奋', count: 25, percentage: 20, color: '#4ECDC4' },
    { emotion: '感动', count: 12, percentage: 10, color: '#A8E6CF' },
    { emotion: '其他', count: 6, percentage: 5, color: '#DDA0DD' }
  ]

  const activityData: ActivityData[] = [
    { activity: '约会', count: 24, hours: 120 },
    { activity: '旅行', count: 8, hours: 192 },
    { activity: '看电影', count: 16, hours: 32 },
    { activity: '做饭', count: 32, hours: 64 },
    { activity: '散步', count: 28, hours: 42 },
    { activity: '运动', count: 20, hours: 40 }
  ]

  const monthlyData: MonthlyData[] = [
    { month: '1月', memories: 3, goals: 2, emotions: 12 },
    { month: '2月', memories: 5, goals: 1, emotions: 14 },
    { month: '3月', memories: 4, goals: 2, emotions: 16 },
    { month: '4月', memories: 6, goals: 1, emotions: 18 },
    { month: '5月', memories: 8, goals: 3, emotions: 20 },
    { month: '6月', memories: 7, goals: 2, emotions: 15 },
    { month: '7月', memories: 9, goals: 1, emotions: 22 },
    { month: '8月', memories: 6, goals: 0, emotions: 19 }
  ]

  const maxMonthlyValue = useMemo(() => {
    return Math.max(
      ...monthlyData.map(d => Math.max(d.memories, d.goals, d.emotions))
    )
  }, [monthlyData])

  const renderPieChart = (data: EmotionData[]) => {
    const radius = 80
    const centerX = 100
    const centerY = 100
    let currentAngle = 0

    return (
      <svg width="200" height="200" className="pie-chart">
        {data.map((item, index) => {
          const angle = (item.percentage / 100) * 360
          const startAngle = currentAngle
          const endAngle = currentAngle + angle
          
          const x1 = centerX + radius * Math.cos((startAngle * Math.PI) / 180)
          const y1 = centerY + radius * Math.sin((startAngle * Math.PI) / 180)
          const x2 = centerX + radius * Math.cos((endAngle * Math.PI) / 180)
          const y2 = centerY + radius * Math.sin((endAngle * Math.PI) / 180)
          
          const largeArcFlag = angle > 180 ? 1 : 0
          
          const pathData = [
            `M ${centerX} ${centerY}`,
            `L ${x1} ${y1}`,
            `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
            'Z'
          ].join(' ')
          
          currentAngle += angle
          
          return (
            <path
              key={index}
              d={pathData}
              fill={item.color}
              stroke="white"
              strokeWidth="2"
            />
          )
        })}
      </svg>
    )
  }

  const renderBarChart = (data: ActivityData[]) => {
    const maxCount = Math.max(...data.map(d => d.count))
    const barWidth = 40
    const chartHeight = 200
    const chartWidth = data.length * (barWidth + 20)

    return (
      <svg width={chartWidth} height={chartHeight + 40} className="bar-chart">
        {data.map((item, index) => {
          const barHeight = (item.count / maxCount) * chartHeight
          const x = index * (barWidth + 20) + 10
          const y = chartHeight - barHeight

          return (
            <g key={index}>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                fill="#FF6B9D"
                rx="4"
              />
              <text
                x={x + barWidth / 2}
                y={chartHeight + 15}
                textAnchor="middle"
                fontSize="12"
                fill="#666"
              >
                {item.activity}
              </text>
              <text
                x={x + barWidth / 2}
                y={y - 5}
                textAnchor="middle"
                fontSize="12"
                fill="#333"
                fontWeight="bold"
              >
                {item.count}
              </text>
            </g>
          )
        })}
      </svg>
    )
  }

  const renderLineChart = (data: MonthlyData[]) => {
    const chartWidth = 600
    const chartHeight = 200
    const padding = 40
    const stepX = (chartWidth - 2 * padding) / (data.length - 1)

    const getY = (value: number) => {
      return chartHeight - padding - ((value / maxMonthlyValue) * (chartHeight - 2 * padding))
    }

    const memoriesPath = data.map((item, index) => {
      const x = padding + index * stepX
      const y = getY(item.memories)
      return index === 0 ? `M ${x} ${y}` : `L ${x} ${y}`
    }).join(' ')

    const goalsPath = data.map((item, index) => {
      const x = padding + index * stepX
      const y = getY(item.goals)
      return index === 0 ? `M ${x} ${y}` : `L ${x} ${y}`
    }).join(' ')

    const emotionsPath = data.map((item, index) => {
      const x = padding + index * stepX
      const y = getY(item.emotions)
      return index === 0 ? `M ${x} ${y}` : `L ${x} ${y}`
    }).join(' ')

    return (
      <svg width={chartWidth} height={chartHeight + 40} className="line-chart">
        {/* 网格线 */}
        {[0, 1, 2, 3, 4].map(i => {
          const y = padding + (i * (chartHeight - 2 * padding)) / 4
          return (
            <line
              key={i}
              x1={padding}
              y1={y}
              x2={chartWidth - padding}
              y2={y}
              stroke="#f0f0f0"
              strokeWidth="1"
            />
          )
        })}
        
        {/* 数据线 */}
        <path d={memoriesPath} fill="none" stroke="#FF6B9D" strokeWidth="3" />
        <path d={goalsPath} fill="none" stroke="#4ECDC4" strokeWidth="3" />
        <path d={emotionsPath} fill="none" stroke="#FFD93D" strokeWidth="3" />
        
        {/* 数据点 */}
        {data.map((item, index) => {
          const x = padding + index * stepX
          return (
            <g key={index}>
              <circle cx={x} cy={getY(item.memories)} r="4" fill="#FF6B9D" />
              <circle cx={x} cy={getY(item.goals)} r="4" fill="#4ECDC4" />
              <circle cx={x} cy={getY(item.emotions)} r="4" fill="#FFD93D" />
              <text
                x={x}
                y={chartHeight + 20}
                textAnchor="middle"
                fontSize="12"
                fill="#666"
              >
                {item.month}
              </text>
            </g>
          )
        })}
      </svg>
    )
  }

  return (
    <div className="data-visualization">
      <div className="visualization-header">
        <h2>数据统计</h2>
        <p>用数据记录我们的美好时光</p>
      </div>

      <div className="tab-navigation">
        <button
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          总览
        </button>
        <button
          className={`tab-btn ${activeTab === 'emotions' ? 'active' : ''}`}
          onClick={() => setActiveTab('emotions')}
        >
          情感分析
        </button>
        <button
          className={`tab-btn ${activeTab === 'activities' ? 'active' : ''}`}
          onClick={() => setActiveTab('activities')}
        >
          活动统计
        </button>
        <button
          className={`tab-btn ${activeTab === 'trends' ? 'active' : ''}`}
          onClick={() => setActiveTab('trends')}
        >
          趋势分析
        </button>
      </div>

      <div className="visualization-content">
        {activeTab === 'overview' && (
          <div className="overview-section">
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">💕</div>
                <div className="stat-info">
                  <h3>{relationshipStats.totalDays}</h3>
                  <p>相爱天数</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">📸</div>
                <div className="stat-info">
                  <h3>{relationshipStats.totalMemories}</h3>
                  <p>美好回忆</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">🎯</div>
                <div className="stat-info">
                  <h3>{relationshipStats.completedGoals}/{relationshipStats.totalGoals}</h3>
                  <p>完成目标</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">😊</div>
                <div className="stat-info">
                  <h3>{relationshipStats.averageHappiness}/10</h3>
                  <p>平均幸福度</p>
                </div>
              </div>
            </div>
            
            <div className="overview-charts">
              <div className="chart-container">
                <h4>情感分布</h4>
                <div className="chart-wrapper">
                  {renderPieChart(emotionData)}
                  <div className="chart-legend">
                    {emotionData.map((item, index) => (
                      <div key={index} className="legend-item">
                        <div className="legend-color" style={{ backgroundColor: item.color }}></div>
                        <span>{item.emotion} ({item.percentage}%)</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'emotions' && (
          <div className="emotions-section">
            <div className="chart-container">
              <h4>情感分布详情</h4>
              <div className="chart-wrapper">
                {renderPieChart(emotionData)}
                <div className="emotion-details">
                  {emotionData.map((item, index) => (
                    <div key={index} className="emotion-item">
                      <div className="emotion-color" style={{ backgroundColor: item.color }}></div>
                      <div className="emotion-info">
                        <h5>{item.emotion}</h5>
                        <p>{item.count} 次记录</p>
                        <div className="emotion-bar">
                          <div 
                            className="emotion-fill" 
                            style={{ 
                              width: `${item.percentage}%`, 
                              backgroundColor: item.color 
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'activities' && (
          <div className="activities-section">
            <div className="chart-container">
              <h4>活动统计</h4>
              <div className="chart-wrapper">
                {renderBarChart(activityData)}
              </div>
            </div>
            <div className="activity-details">
              {activityData.map((item, index) => (
                <div key={index} className="activity-item">
                  <h5>{item.activity}</h5>
                  <div className="activity-stats">
                    <span>{item.count} 次</span>
                    <span>{item.hours} 小时</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'trends' && (
          <div className="trends-section">
            <div className="chart-container">
              <h4>月度趋势</h4>
              <div className="chart-wrapper">
                {renderLineChart(monthlyData)}
                <div className="trend-legend">
                  <div className="legend-item">
                    <div className="legend-line" style={{ backgroundColor: '#FF6B9D' }}></div>
                    <span>回忆</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-line" style={{ backgroundColor: '#4ECDC4' }}></div>
                    <span>目标</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-line" style={{ backgroundColor: '#FFD93D' }}></div>
                    <span>情感记录</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default DataVisualization